import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, ModalityBadge, catColor } from "@/components/Shared";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash2, Pencil } from "lucide-react";

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);

  const load = async () => {
    try { const r = await api.get("/staff"); setStaff(r.data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try { await api.delete(`/staff/${id}`); toast.success("Tech removed"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <PageHeader title="Staff Directory" subtitle="// Manage radiology techs">
        <button data-testid="add-tech-btn" onClick={() => { setEdit(null); setOpen(true); }}
          className="flex items-center gap-2 px-4 py-2 border border-[#D902EE] bg-[#D902EE]/10 text-white text-sm font-mono-rs uppercase tracking-wider hover:bg-[#D902EE]/25 transition-all duration-300">
          <Plus className="w-4 h-4" /> Add Tech
        </button>
      </PageHeader>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="staff-grid">
        {staff.map((t) => (
          <div key={t.id} data-testid={`staff-card-${t.id}`}
            className={`bg-[#0F1219] border-l-2 border border-slate-800 p-5 group hover:border-[#D902EE]/40 transition-all duration-300`}
            style={{ borderLeftColor: catColor(t.category) }}>
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white font-semibold text-lg">{t.name}</div>
                <div className="text-xs text-slate-500 mb-2">{t.email}</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <ModalityBadge modality={t.category} />
                  {t.shift_preference && t.shift_preference !== "any" && (
                    <span data-testid={`staff-pref-${t.id}`} className="text-[10px] font-mono-rs uppercase tracking-wider text-slate-400 border border-slate-700 px-1.5 py-0.5">
                      {t.shift_preference} pref
                    </span>
                  )}
                  <span data-testid={`staff-ft-${t.id}`} className={`text-[10px] font-mono-rs uppercase tracking-wider px-1.5 py-0.5 border ${
                    t.full_time === false ? "text-slate-400 border-slate-700" : "text-[#34E5B0] border-[#34E5B0]/50 bg-[#34E5B0]/10"}`}>
                    {t.full_time === false ? "Part-time" : "Full-time"}
                  </span>
                </div>
              </div>
              <div className="flex gap-2 opacity-60 group-hover:opacity-100 transition-opacity">
                <button data-testid={`edit-tech-${t.id}`} onClick={() => { setEdit(t); setOpen(true); }}
                  className="text-slate-500 hover:text-[#00F0FF]"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-tech-${t.id}`} onClick={() => remove(t.id)}
                  className="text-slate-500 hover:text-[#FF003C]"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {staff.length === 0 && <p className="text-slate-500 text-sm">No techs added yet.</p>}
      </div>

      <TechModal open={open} onOpenChange={setOpen} tech={edit} onSaved={load} />
    </div>
  );
}

function TechModal({ open, onOpenChange, tech, onSaved }) {
  const [form, setForm] = useState({});
  useEffect(() => {
    setForm({ name: tech?.name || "", email: tech?.email || "", category: tech?.category || "CT", password: "", shift_preference: tech?.shift_preference || "any", full_time: tech?.full_time !== false });
  }, [tech, open]);
  const isEdit = tech && tech.id;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const inputCls = "w-full bg-[#05060A] border border-slate-700 px-3 py-2 text-sm text-white outline-none focus:border-[#00F0FF] transition-colors";

  const save = async () => {
    try {
      if (isEdit) {
        const payload = { name: form.name, category: form.category, shift_preference: form.shift_preference, full_time: form.full_time };
        if (form.password) payload.password = form.password;
        await api.put(`/staff/${tech.id}`, payload);
        toast.success("Tech updated");
      } else {
        await api.post("/staff", { name: form.name, email: form.email, category: form.category, password: form.password || "tech123", shift_preference: form.shift_preference, full_time: form.full_time });
        toast.success("Tech created");
      }
      onOpenChange(false); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F1219] border border-[#D902EE]/40 text-white rounded-none max-w-md" data-testid="tech-modal">
        <DialogHeader><DialogTitle className="font-mono-rs uppercase tracking-wider">{isEdit ? "Edit Tech" : "Add Tech"}</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Name</label>
            <input data-testid="tech-name-input" value={form.name || ""} onChange={(e) => set("name", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Email</label>
            <input data-testid="tech-email-input" type="email" value={form.email || ""} disabled={isEdit}
              onChange={(e) => set("email", e.target.value)} className={`${inputCls} ${isEdit ? "opacity-50" : ""}`} />
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Category</label>
            <div className="grid grid-cols-3 gap-2">
              {["CT", "X-ray", "Both"].map((m) => (
                <button key={m} data-testid={`tech-cat-${m}`} onClick={() => set("category", m)}
                  className={`py-2 text-xs font-mono-rs uppercase border transition-all ${
                    form.category === m
                      ? (m === "CT" ? "border-[#D902EE] bg-[#D902EE]/15 text-white"
                         : m === "X-ray" ? "border-[#00F0FF] bg-[#00F0FF]/15 text-white"
                         : "border-[#8A2BE2] bg-[#8A2BE2]/20 text-white")
                      : "border-slate-700 text-slate-400"}`}>{m}</button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Shift Preference</label>
            <div className="grid grid-cols-3 gap-2">
              {["day", "night", "any"].map((p) => (
                <button key={p} data-testid={`tech-pref-${p}`} onClick={() => set("shift_preference", p)}
                  className={`py-2 text-xs font-mono-rs uppercase border transition-all ${
                    form.shift_preference === p ? "border-[#B15CFF] bg-[#8A2BE2]/20 text-white" : "border-slate-700 text-slate-400"}`}>{p}</button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Night shifts require a dual-cert (Both) tech; day-only preferred otherwise.</p>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Employment</label>
            <div className="grid grid-cols-2 gap-2">
              {[["Full-time", true], ["Part-time", false]].map(([label, val]) => (
                <button key={label} data-testid={`tech-ft-${val ? "full" : "part"}`} onClick={() => set("full_time", val)}
                  className={`py-2 text-xs font-mono-rs uppercase border transition-all ${
                    form.full_time === val ? "border-[#34E5B0] bg-[#34E5B0]/15 text-white" : "border-slate-700 text-slate-400"}`}>{label}</button>
              ))}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">Full-time techs are guaranteed ≥38h per week in auto-generated schedules.</p>
          </div>
          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-slate-400 mb-1 block">Password {isEdit && "(leave blank to keep)"}</label>
            <input data-testid="tech-password-input" type="password" value={form.password || ""} onChange={(e) => set("password", e.target.value)}
              placeholder={isEdit ? "unchanged" : "tech123"} className={inputCls} />
          </div>
        </div>
        <DialogFooter>
          <button data-testid="tech-save-btn" onClick={save}
            className="w-full py-2 font-mono-rs uppercase tracking-wider text-sm border border-[#D902EE] bg-[#D902EE]/10 hover:bg-[#D902EE]/25 transition-all duration-300">
            {isEdit ? "Save" : "Create Tech"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
